/**
 * Which build is this? — the answer to "did my phone load the new one?"
 *
 * WHY IT EXISTS. This is an installed PWA behind a service worker, so the code
 * running on a device is not necessarily the code that was last deployed: a
 * stale shell can survive a deploy (that failure mode is documented at
 * `local-purge.ts#APP_SHELL_BUILD_ID` — it is what the per-build cache name was
 * introduced to fix). Without a visible version there is no way to tell "the fix
 * does not work" apart from "the fix is not loaded", which makes every on-device
 * report ambiguous.
 *
 * The three values are injected by Vite `define` at build time
 * (`apps/web/vite.config.ts`):
 *
 *   commit   the deploy's git SHA — CI passes the SAME `github.sha` it tags the
 *            Cloud Run image with, so this string can be compared directly
 *            against the deployed revision. `dev` when built without git.
 *   builtAt  wall-clock of the build. The field that needs no external
 *            reference: newer than your deploy means you have the new one.
 *   buildId  the shell-cache rotation id, shown last because it is the value
 *            that appears in Cache Storage names when debugging.
 *
 * `typeof` guards rather than bare reads: an un-defined identifier would be a
 * ReferenceError at module scope, and a missing `define` must degrade to
 * "unknown", never break the app (the same pattern local-purge.ts uses).
 *
 * @module
 */

declare const __APP_BUILD_ID__: string | undefined;
declare const __APP_COMMIT__: string | undefined;
declare const __APP_BUILT_AT__: string | undefined;

function injected(value: string | undefined, fallback: string): string {
  return typeof value === 'string' && value !== '' ? value : fallback;
}

/** Short git SHA of the build, or `dev`. */
export const APP_COMMIT: string = injected(
  typeof __APP_COMMIT__ === 'undefined' ? undefined : __APP_COMMIT__,
  'dev',
);

/** ISO timestamp of the build, or '' when unknown. */
export const APP_BUILT_AT: string = injected(
  typeof __APP_BUILT_AT__ === 'undefined' ? undefined : __APP_BUILT_AT__,
  '',
);

/** The shell-cache rotation id (also the Cache Storage name suffix). */
export const APP_BUILD_ID: string = injected(
  typeof __APP_BUILD_ID__ === 'undefined' ? undefined : __APP_BUILD_ID__,
  'dev',
);

/**
 * `2026-07-26 09:41 UTC` — minute precision, no seconds, explicitly UTC so a
 * value read on a phone in one timezone can be compared with a deploy log in
 * another. Returns '' for an absent or unparseable stamp rather than printing
 * `Invalid Date` at the user.
 *
 * @param iso an ISO-8601 timestamp
 */
export function formatBuiltAt(iso: string): string {
  if (iso === '') return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  const date = `${String(at.getUTCFullYear())}-${pad(at.getUTCMonth() + 1)}-${pad(at.getUTCDate())}`;
  return `${date} ${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())} UTC`;
}

/**
 * The one-line version string for the Settings readout, e.g.
 * `5446979 · 2026-07-26 09:41 UTC`. Degrades to whatever is known.
 *
 * @returns the label; never empty (falls back to the build id)
 */
export function appVersionLabel(): string {
  const parts = [APP_COMMIT, formatBuiltAt(APP_BUILT_AT)].filter((part) => part !== '');
  return parts.length === 0 ? APP_BUILD_ID : parts.join(' · ');
}
